import { useState, useEffect, useCallback, useRef } from 'react';
import axiosInstance from '../api/axiosInstance';
// import { toast } from 'react-toastify'; // Commented out - using react-toast now
import { toast } from 'react-toast';
import { debounce } from 'lodash';
import { useLocation, useSearchParams } from 'react-router';

const getPageFromSearchParams = (searchParams) => {
  const page = parseInt(searchParams.get('page'), 10);
  return Number.isFinite(page) && page > 0 ? page : 1;
};

const useProjectData = (initialPage = 1, initialStatus = 'active', initialPageSize = 10) => {
  const [projects, setProjects] = useState([]);
  const [filteredProjects, setFilteredProjects] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const currentPage = getPageFromSearchParams(searchParams) || initialPage;

  const setCurrentPage = useCallback((page) => {
    const validPage = Math.max(1, Number(page) || 1);
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (validPage <= 1) {
          next.delete('page');
        } else {
          next.set('page', String(validPage));
        }
        return next;
      },
      { replace: true }
    );
  }, [setSearchParams]);

  const [totalPages, setTotalPages] = useState(0);
  const [pageSize, setPageSize] = useState(initialPageSize);
  const pageSizeRef = useRef(pageSize);
  pageSizeRef.current = pageSize;
  const locationRef = useRef(location);
  locationRef.current = location;
  const [activeTab, setActiveTab] = useState('active');
  const [searchTerm, setSearchTerm] = useState('');
  const [statusCounts, setStatusCounts] = useState({
    all: 0,
    active: 0,
    inactive: 0,
    archived: 0
  });
  const [isBitbucketConfigured, setIsBitbucketConfigured] = useState(false);

  const transformProjectData = (projectData) => {
    return projectData
    .map(project => ({
      id: project.project_id,
      name: project.project_name,
      description: project.description || '',
      type: project.project_type || 'None',
      status: project.status || 'active',
      progress: project.progress || 0,
      createdAt: project.created_at,
      createdBy: project.created_by?.name || 'Unknown',
      updatedAt: project.updated_at,
      owner: project.created_by?.name || 'Unknown',
      testCases: project.test_case_count || 0,
      // Preserve null/undefined — do not coerce missing pass_rate to 0
      passRate:
        project.pass_rate == null || project.pass_rate === ''
          ? null
          : Number(project.pass_rate),
      originalData: project
    }));
  };


  const debouncedFetchProjects = useCallback( 
    debounce(async (searchValue, page, status, sortConfig, sizeOverride) => {
    try {
      setIsLoading(true);
      const effectivePageSize = Number(sizeOverride) > 0 ? Number(sizeOverride) : pageSizeRef.current;
      
      const queryParams = {
        page: page,
        page_size: effectivePageSize
      };

    if (searchValue && searchValue.trim() !== '') {
        queryParams.search = searchValue.trim();
      }
      const path = locationRef?.current?.pathname || window.location.pathname;
      if (path.includes('/dashboard')) {
        const workspaceId = parseInt(path.split('/')[1]?.split('_')[1], 10);
        if (workspaceId) {
          queryParams.workspace_id = workspaceId;
        }
      }

    if (status && status !== 'all') {
        queryParams.pro_status = status;
      }


      // Add sorting parameters
      if (sortConfig && sortConfig.sort_type && sortConfig.sort_order) {
        queryParams.sort_type = sortConfig.sort_type;
        queryParams.sort_order = sortConfig.sort_order;
      }

      const response = await axiosInstance.get('/projects/list_project', {
        params: queryParams,
        paramsSerializer: params => {
          return Object.entries(params)
            .map(([key, value]) => `${key}=${encodeURIComponent(value)}`)
            .join('&');
        }
      });


      // console.log('API response:', response);

      const allProjects = response?.projects || [];
      setIsBitbucketConfigured(response?.is_bitbucket_configured === true);
      
      const counts = {
        all: allProjects.length,
        active: response?.active_count ,
        inactive: response?.inactive_count,
        archived: response?.archived_count
      };
      
      setStatusCounts(counts);
  
      let filteredData = allProjects;
      if (status && status !== 'all') {
        filteredData = allProjects.filter(project => project.status === status);
      }
  
      const apiTotalPages = Number(response?.total_pages) || 0;
      setTotalPages(apiTotalPages);

      if (apiTotalPages > 0 && page > apiTotalPages) {
        setSearchParams(
          (prev) => {
            const next = new URLSearchParams(prev);
            if (apiTotalPages <= 1) {
              next.delete('page');
            } else {
              next.set('page', String(apiTotalPages));
            }
            return next;
          },
          { replace: true }
        );
        return;
      }
  
      const paginatedProjects = filteredData

      const transformedProjects = transformProjectData(paginatedProjects);
      
      setProjects(transformedProjects);
      setFilteredProjects(transformedProjects);
      setError(null);
    } catch (err) {
      console.error('Error fetching projects:', err);
      setError(err.message || 'Error fetching projects');
      setIsBitbucketConfigured(false);
      setProjects([]);
      setFilteredProjects([]);
    } finally {
      setIsLoading(false);
    }
  },500),
[]
);

  useEffect(() => {
    if (location.pathname.includes('/projects')) {
      setTotalPages(0);
    }
  }, [location.pathname]);

  useEffect(() => {
    if (!location.pathname.includes('/projects') || totalPages <= 0) return;

    setSearchParams(
      (prev) => {
        const urlPage = getPageFromSearchParams(prev);
        if (urlPage <= totalPages) return prev;

        const next = new URLSearchParams(prev);
        if (totalPages <= 1) {
          next.delete('page');
        } else {
          next.set('page', String(totalPages));
        }
        return next;
      },
      { replace: true }
    );
  }, [totalPages, location.pathname, setSearchParams]);

  useEffect(() => {
    return () => {
      debouncedFetchProjects.cancel();
    };
  }, [debouncedFetchProjects]);

  const fetchProjects = useCallback((page = currentPage, status = activeTab, searchValue = searchTerm, sortConfig = null, size = pageSizeRef.current) => {
  debouncedFetchProjects.cancel();
  debouncedFetchProjects(searchValue, page, status, sortConfig, size);
}, [debouncedFetchProjects, searchTerm, currentPage, activeTab]);

  const updateProjectStatus = async (projectId, newStatus) => {
    try {
          const oldProject = projects.find(p => p.id === projectId);
        setProjects(prevProjects =>
      prevProjects.map(project =>
        project.id === projectId
          ? {
              ...project,
              status: newStatus,
              updatedAt: new Date().toISOString()
            }
          : project
      )
    );
        setFilteredProjects(prevProjects =>
      prevProjects.map(project =>
        project.id === projectId
          ? {
              ...project,
              status: newStatus,
              updatedAt: new Date().toISOString()
            }
          : project
      )
    );

   setStatusCounts(prev => {
      const oldStatus = oldProject?.status;
      const newCounts = {...prev};
      
      if (oldStatus && oldStatus !== newStatus) {
        if (oldStatus in newCounts) newCounts[oldStatus]--;
        if (newStatus in newCounts) newCounts[newStatus]++;
        newCounts.all = newCounts.active + newCounts.inactive + newCounts.archived;
      }
      
      return newCounts;
    });
       const endpoint = newStatus === 'archived' 
      ? '/projects/archive_project' 
      : '/projects/unarchive_project';
      const response = await axiosInstance.post(
        endpoint,
        {
          project_id: projectId,
          status: newStatus
        }
      );
      

    if (response.status_code === 200) {
    
      await fetchProjects(currentPage, activeTab, searchTerm);
      // toast.success(response.message || `Project status updated to ${newStatus}`);
      return true;
    }
    
      throw new Error(response.message || 'Failed to update project status');
    } catch (err) {
          await fetchProjects(currentPage, activeTab, searchTerm);
      const errorMessage = err.response?.detail?.message || err.message;
      console.error('Status update error:', err);
      toast.error(`Failed to update project status: ${errorMessage}`);
      return false;
    }
  };

  const deleteProject = async (projectId) => {
    try {
      await axiosInstance.delete(`/projects/delete_project?project_id=${projectId}`);

      setProjects(prevProjects => prevProjects.filter(p => p.id !== projectId));
      setFilteredProjects(prevProjects => prevProjects.filter(p => p.id !== projectId));

      const deletedProject = projects.find(p => p.id === projectId);
      if (deletedProject && deletedProject.status in statusCounts) {
        setStatusCounts(prev => ({
          ...prev,
          [deletedProject.status]: prev[deletedProject.status] - 1,
          all: prev.all - 1
        }));
      }

      toast.success('Project deleted successfully');
      return true;
    } catch (err) {
      console.error('Delete error:', err);
      toast.error(`Failed to delete project: ${err.message}`);
      return false;
    }
  };

  const duplicateProject = async (projectId, newProjectName) => {
    try {
      const response = await axiosInstance.post(
        '/projects/duplicate_project',
        {
          project_id: projectId,
          new_name: newProjectName
        }
      );

      if (response?.project) {
        const newProject = transformProjectData([response.project])[0];
        
        setProjects(prev => [...prev, newProject]);
        setFilteredProjects(prev => [...prev, newProject]);

        setStatusCounts(prev => ({
          ...prev,
          [newProject.status]: (prev[newProject.status] || 0) + 1,
          all: prev.all + 1
        }));

        // toast.success(`Project duplicated successfully: ${newProjectName}`);
        return newProject;
      }
      throw new Error('Invalid response format');
    } catch (err) {
      console.error('Duplication error:', err);
      toast.error(`Failed to duplicate project: ${err.message}`);
      return null;
    }
  };

  const updateProject = async (projectId, updatedData) => {
    try {
      const response = await axiosInstance.put(
        `/projects/update_project?project_id=${projectId}`,
        {
          project_name: updatedData.name,
          description: updatedData.description,
          project_type: updatedData.type,
          status: updatedData.status,
        }
      );

      if (response.status_code === 200) {
        const updatedProject = {
          ...projects.find(p => p.id === projectId),
          ...updatedData,
          updatedAt: new Date().toISOString()
        };

        setProjects(prevProjects =>
          prevProjects.map(project =>
            project.id === projectId ? updatedProject : project
          )
        );
        
        setFilteredProjects(prevProjects =>
          prevProjects.map(project =>
            project.id === projectId ? updatedProject : project
          )
        );

        // toast.success('Project updated successfully!');
        return true;
      }
      throw new Error(response.message || 'Failed to update project');
    } catch (err) {
      console.error('Update error:', err);
      toast.error(`Failed to update project: ${err.message}`);
      return false;
    }
  };


  return {
    projects,
    searchTerm,
    setSearchTerm,
    filteredProjects,
    isLoading,
    error,
    currentPage,
    totalPages,
    pageSize,
    activeTab,
    statusCounts,
    isBitbucketConfigured,
    setCurrentPage,
    setPageSize,
    setActiveTab,
    setFilteredProjects,
    fetchProjects,
    updateProjectStatus,
    deleteProject,
    duplicateProject,
    updateProject
  };
};

export default useProjectData;